import React from 'react'
import { Link } from 'react-router-dom'

/**
 * Public terms of service page (no auth). Referenced from the OAuth consent
 * screen URL field, so it MUST be publicly reachable at marzz.ai/terms before
 * submitting the Google Business Profile API access request form.
 *
 * Template only — review with legal counsel before relying on it in production.
 */
const EFFECTIVE_DATE = 'June 6, 2026'

const Terms: React.FC = () => (
  <div className="max-w-3xl mx-auto px-4 py-12 text-gray-800 leading-relaxed">
    <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
    <p className="text-sm text-gray-500 mb-8">Effective date: {EFFECTIVE_DATE}</p>

    <Section title="1. Acceptance of these terms">
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) are an agreement between you and Marzz AI
        (&ldquo;Marzz&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;), an entity of ExploringNotBoring,
        governing your use of the Marzz platform, websites, and related services (collectively, the
        &ldquo;Service&rdquo;). By creating an account or using the Service, you agree to be bound
        by these Terms and our{' '}
        <Link to="/privacy" className="text-primary-700 hover:underline">
          Privacy Policy
        </Link>
        . If you don&rsquo;t agree, please don&rsquo;t use the Service.
      </p>
    </Section>

    <Section title="2. What Marzz does">
      <p>
        Marzz is a Local SEO marketing platform. It helps agencies and businesses connect their
        Google Business Profile, GoHighLevel CRM, and other tools to generate keyword research,
        local search ranking reports, lead analytics, and shareable client dashboards. Specific
        features may change over time as we improve the Service.
      </p>
    </Section>

    <Section title="3. Eligibility and accounts">
      <ul className="list-disc pl-6 space-y-2">
        <li>
          You must be at least 16 years old and able to enter a binding contract to use Marzz.
        </li>
        <li>
          You&rsquo;re responsible for keeping your account credentials secure and for activity
          under your account. Notify us at{' '}
          <a className="text-primary-700 hover:underline" href="mailto:support@marzz.ai">
            support@marzz.ai
          </a>{' '}
          if you suspect unauthorized access.
        </li>
        <li>
          When you connect a third-party account (e.g. Google Business Profile or GoHighLevel), you
          confirm you have authorization to do so for the businesses involved.
        </li>
      </ul>
    </Section>

    <Section title="4. Acceptable use">
      <p className="mb-3">You agree not to:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Use the Service to violate laws, infringe rights, or harm others.</li>
        <li>Attempt to access accounts, data, or systems you&rsquo;re not authorized to access.</li>
        <li>
          Interfere with the Service or attempt to exceed published rate limits in a way that
          degrades the experience for other users.
        </li>
        <li>Reverse-engineer or copy non-public portions of the Service.</li>
        <li>
          Use the Service in a way that breaches the policies of any third-party API it integrates
          with, including the{' '}
          <a
            className="text-primary-700 hover:underline"
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>
          .
        </li>
      </ul>
    </Section>

    <Section title="5. Third-party services">
      <p>
        The Service integrates with third-party services such as Google Business Profile,
        GoHighLevel, SendGrid, Stripe, AWS, and Mapbox. Your use of those services is subject to
        their own terms and privacy policies. Marzz is not responsible for the availability,
        content, or behavior of third-party services.
      </p>
    </Section>

    <Section title="6. Subscriptions, fees, and billing">
      <p>
        Some Marzz features are available on a paid subscription basis. Pricing, billing cycle, and
        included usage are disclosed before you subscribe. Subscriptions renew automatically unless
        cancelled before the renewal date. You can cancel at any time from your account settings;
        cancellation takes effect at the end of the current billing period. Fees already paid are
        non-refundable except where required by law.
      </p>
    </Section>

    <Section title="7. Customer data and ownership">
      <p>
        You retain all rights to the data you submit to or generate through the Service
        (&ldquo;Customer Data&rdquo;). You grant Marzz a worldwide, non-exclusive license to host,
        copy, process, and display Customer Data solely to operate and improve the Service for you.
        We treat Customer Data as confidential and handle it as described in our{' '}
        <Link to="/privacy" className="text-primary-700 hover:underline">
          Privacy Policy
        </Link>
        .
      </p>
    </Section>

    <Section title="8. Marzz intellectual property">
      <p>
        Marzz and its licensors retain all rights, title, and interest in and to the Service,
        including all software, designs, trademarks, and documentation. These Terms do not grant you
        any right or license to Marzz&rsquo;s intellectual property except as needed to use the
        Service.
      </p>
    </Section>

    <Section title="9. Beta and early-access features">
      <p>
        We may make features available on a beta, preview, or early-access basis. Such features are
        provided &ldquo;as is&rdquo;, may change at any time, and may have limited or no support.
        Don&rsquo;t rely on them for production-critical workflows without confirming behavior
        first.
      </p>
    </Section>

    <Section title="10. Disclaimers">
      <p>
        Except as expressly stated in these Terms, the Service is provided &ldquo;as is&rdquo; and
        &ldquo;as available&rdquo; without warranties of any kind, whether express, implied, or
        statutory, including warranties of merchantability, fitness for a particular purpose, or
        non-infringement. Marzz does not warrant that the Service will be uninterrupted, error-free,
        or that it will meet your specific requirements.
      </p>
    </Section>

    <Section title="11. Limitation of liability">
      <p>
        To the maximum extent permitted by law, Marzz and its affiliates will not be liable for any
        indirect, incidental, special, consequential, or punitive damages, or for any loss of
        profits, revenues, data, or goodwill, arising from or related to the Service. Our total
        cumulative liability for any claim arising from these Terms or your use of the Service will
        not exceed the greater of (a) the fees you paid to Marzz in the twelve months preceding the
        event giving rise to the claim, or (b) one hundred US dollars (US$100).
      </p>
    </Section>

    <Section title="12. Termination">
      <p>
        You can stop using the Service and delete your account at any time. We may suspend or
        terminate your access if you breach these Terms, if required by law, or if continuing to
        provide the Service to you creates undue risk for us or others. On termination, your right
        to use the Service ends; sections that by their nature should survive termination (e.g.
        ownership, disclaimers, limitation of liability) will survive.
      </p>
    </Section>

    <Section title="13. Changes to the Service or these Terms">
      <p>
        We may update the Service and these Terms from time to time. For material changes to the
        Terms, we&rsquo;ll update the effective date at the top of this page and, where appropriate,
        provide notice in-product or by email. Your continued use of the Service after the effective
        date constitutes acceptance of the revised Terms.
      </p>
    </Section>

    <Section title="14. Governing law">
      <p>
        These Terms are governed by the laws of the State of Delaware, USA, without regard to its
        conflict-of-laws principles. Disputes that can&rsquo;t be resolved informally will be
        brought exclusively in the state or federal courts located in Delaware, and you and Marzz
        consent to the personal jurisdiction of those courts.
      </p>
    </Section>

    <Section title="15. Contact">
      <p>
        Questions about these Terms? Email{' '}
        <a className="text-primary-700 hover:underline" href="mailto:support@marzz.ai">
          support@marzz.ai
        </a>
        .
      </p>
    </Section>

    <div className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-500">
      See also:{' '}
      <Link to="/privacy" className="text-primary-700 hover:underline">
        Privacy Policy
      </Link>
    </div>
  </div>
)

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
)

export default Terms
